"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useQueryClient } from "@tanstack/react-query"
import { StreamChat } from "stream-chat"

import { LoadingFullScreen } from "../../components/LoadingFullScreen"
import { useClient } from "./hooks/useClient"
import { useLazyChannelLists } from "./hooks/useLazyChannelLists"
import { useSimpleUnreadCount } from "./hooks/useSimpleUnreadCount"

type MessagingContextType = {
  client: StreamChat<any>
  totalUnreadCount: number
  mainInboxChannelIds: string[]
  messageRequestsChannelIds: string[]
  isConnected: boolean
  isError: boolean
  isLoadingUnreadCount: boolean
  isLoadingChannelLists: boolean
  currentChannelId: string | undefined
  setCurrentChannelId: (newChannelId: string | undefined) => void
  enableChannelLists: () => void
}

type MessagingProviderProps = {
  children: React.ReactNode
  config: {
    apiKey: string
  }
}

export const MessagingContext = createContext<MessagingContextType | null>(null)

export const useMessagingContext = () => {
  const ctx = useContext(MessagingContext)
  if (!ctx) {
    throw new Error(
      "useMessagingContext must be used within a MessagingProvider",
    )
  }
  return ctx
}

export const MessagingProvider = ({
  children,
  config: { apiKey },
}: MessagingProviderProps) => {
  const { client, error, isLoading: isConnecting } = useClient(apiKey)
  const queryClient = useQueryClient()

  const [currentChannelId, setCurrentChannelId] = useState<string | undefined>(
    undefined,
  )

  // Track if we've ever connected - only show loading on initial connection
  const hasConnectedRef = useRef(false)
  if (client && !isConnecting && !hasConnectedRef.current) {
    hasConnectedRef.current = true
  }

  // Track whether channel lists should be fetched (lazy loading)
  // Use ref to track if already enabled to prevent duplicate calls
  const channelListsEnabledRef = useRef(false)
  const [channelListsEnabled, setChannelListsEnabled] = useState(false)

  // Stable callback that only enables once.
  // Invalidates the allChannels cache to ensure fresh data — the initial
  // query may have fired before the backend created the user's channels
  // (e.g., during signup), returning a stale empty result.
  const enableChannelLists = useCallback(() => {
    if (!channelListsEnabledRef.current) {
      channelListsEnabledRef.current = true
      void queryClient.invalidateQueries({ queryKey: ["allChannels"] })
      setChannelListsEnabled(true)
    }
  }, [queryClient])

  // Simple unread count - uses Stream events, no backend API calls
  const { unreadCount, isLoading: isLoadingUnreadCount } =
    useSimpleUnreadCount(client)

  // Channel lists are only fetched when enabled (on messenger page)
  const {
    mainInboxChannelIds,
    messageRequestsChannelIds,
    isLoading: isLoadingChannelLists,
  } = useLazyChannelLists(client, channelListsEnabled)

  // When a user gets a message in a new channel, we need to re-fetch.
  // Always invalidate the allChannels cache so stale results don't persist
  // when the user later navigates to the messenger page.
  useEffect(() => {
    if (!client) return

    const handleNewChannel = async () => {
      await queryClient.invalidateQueries({ queryKey: ["allChannels"] })
      if (channelListsEnabledRef.current) {
        await queryClient.invalidateQueries({ queryKey: ["message-channel"] })
      }
    }

    client.on("notification.added_to_channel", handleNewChannel)

    return () => {
      client.off("notification.added_to_channel", handleNewChannel)
    }
  }, [client, queryClient])

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      client,
      totalUnreadCount: unreadCount,
      mainInboxChannelIds,
      messageRequestsChannelIds,
      isConnected: !!client,
      isError: !!error,
      isLoadingUnreadCount,
      isLoadingChannelLists,
      currentChannelId,
      setCurrentChannelId,
      enableChannelLists,
    }),
    [
      client,
      unreadCount,
      mainInboxChannelIds,
      messageRequestsChannelIds,
      error,
      isLoadingUnreadCount,
      isLoadingChannelLists,
      currentChannelId,
      enableChannelLists,
    ],
  )

  // Only show loading during initial connection, not during reconnects
  if (isConnecting && !hasConnectedRef.current) return <LoadingFullScreen />

  return (
    <MessagingContext.Provider value={contextValue}>
      {children}
    </MessagingContext.Provider>
  )
}
